<script setup>

import DefaultButton from "@/components/global-themed/buttons/DefaultButton.vue";
import CustomMultiselect from "@/components/global-themed/CustomMultiselect.vue";
import {computed, ref, watch} from "vue";
import {useMutation} from "@tanstack/vue-query";
import axios from "axios";
import {errorToast, successToast} from "@/composables/useToasts";
import useWebsite from "@/composables/useWebsite";
import {useStore} from "vuex";

defineProps({
  reasons: {
    type: Object,
    required: true,
  },
});

const reason = ref('');
const completedActionsTimeInMinutes = ref(0);

const {websiteId} = useWebsite();
const store = useStore();

watch(websiteId, async function () {
  completedActionsTimeInMinutes.value = await store.dispatch('onsiteEditor/fetchCompletedActionsTime', websiteId.value);
})

const calculateTime = (value) => {
  if (value < 60) {
    return `${value} minute${value === 1 ? '' : 's'}`
  }
  const hours = Math.round(value/60)

  return `${hours} hour${hours === 1 ? '' : 's'}`
};

const completedActionsTimeText = computed(() => calculateTime(completedActionsTimeInMinutes.value));

const {mutate: deleteWebsite, isPending} = useMutation({
  mutationFn: async () => await axios.post(`/ajax/v2/billing/package/website/delete`, {
    reason: reason.value,
  }),
  onError: () => {
    errorToast('Something went wrong');
  },
  onSuccess: ({data}) => {
    successToast(data.message);

    window.location.href = data.redirectUrl;
  },
});
</script>

<template>
  <div class="tw-m-auto tw-max-w-default-container">
    <div class="tw-bg-white tw-px-64 tw-py-18 tw-rounded-lg tw-flex tw-flex-col tw-gap-y-8 tw-items-center">
      <h1 class="tw-font-bold tw-text-4.5xl tw-text-neutral-900">
        Do you want to remove the website?
      </h1>

      <div class="tw-space-y-4 tw-text-base tw-text-neutral-900">
        <div>
          <p>
            Deleting this website will result in all information (relating to this website) to be permanently deleted
            immediately, including;
          </p>
          <ul class="tw-list-disc tw-list-inside">
            <li>Keyword Strategy</li>
            <li>Keyword Ideas</li>
            <li>Keyword Tracking</li>
            <li>Website Actions</li>
            <li>Backlink Analysis</li>
            <li>Content Ideas</li>
          </ul>
        </div>

        <div class="tw-text-xl tw-font-bold tw-leading-9">
          Are You Sure You Want to Say Goodbye to the Onsite Optimizer?
        </div>

        <p>
          <span class="tw-font-bold">Important Notice:</span>
          You are actively using the Onsite Optimizer for this website. Removing the website from your account will
          disable your ability to make new edits or amend existing changes. However, all changes you've implemented
          will remain effective as long as your Hike Agency account is active.
        </p>

        <p>
          As long as you maintain your Hike Agency account, any changes made via the Onsite Optimizer will stay
          intact on the client's site, provided the script remains installed. If you decide to remove the script,
          we strongly suggest you download a CSV file of all SEO adjustments you've made. This record will assist
          you or your client in manually reimplementing these changes if necessary.
        </p>

        <div class="tw-flex tw-justify-between tw-items-center tw-py-3">
          <p>
            <span class="tw-font-bold">Estimated Time to Manually Reimplement:</span> <span class="tw-font-bold tw-text-primary">{{ completedActionsTimeText }}</span>.
          </p>
          <DefaultButton
            @click="store.dispatch('onsiteEditor/fetchChangesCsv', websiteId)"
          >
            Download CSV File
          </DefaultButton>
        </div>

        <p class="tw-text-center">
          This information is <strong class="tw-text-danger">permanently deleted</strong> and the data cannot be
          retrieved if you change your mind.
        </p>
      </div>

      <div class="tw-w-96">
        <CustomMultiselect
          v-model="reason"
          class="tw-text-left tw-w-full"
          :close-on-select="true"
          :options="reasons"
          :searchable="true"
          :can-deselect="false"
        />
      </div>

      <DefaultButton
        variant="danger"
        :loading="isPending"
        :disabled="!reason || isPending"
        @click="deleteWebsite"
      >
        Yes Please Delete Now
      </DefaultButton>
    </div>
  </div>
</template>
